import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaBalanceScale, FaChartLine, FaLaptopCode, FaLightbulb, FaShieldAlt, FaHandshake, FaUserShield, FaSearchDollar, FaFileAlt, FaCheckCircle, FaUniversity, FaClipboardList, FaEdit, FaGraduationCap } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FAQContainer = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
  background: white;
  padding: 40px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px;
  }
`;

const FAQContent = styled.div`
  flex: 1;
  animation: ${slideInRight} 1s ease-in-out;
`;

const FAQItem = styled.div`
  background: #ffffff;
  margin: 15px 0;
  border-bottom: 1px solid #d1d5db;
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  opacity: 0;
  transform: translateY(20px);
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};

  &:hover {
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const Question = styled.div`
  background: #f9fafb;
  padding: 15px;
  font-size: 18px;
  font-weight: bold;
  color: #333;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.3s ease;

  &:hover {
    background: #eef1f6;
  }
`;

const Answer = styled.div`
  padding: 15px;
  font-size: 16px;
  color: #555;
  background: #ffffff;
  border-top: 1px solid #d1d5db;
  animation: ${fadeIn} 0.5s ease-in-out;
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  margin-bottom: 30px;
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  font-size: 42px;
  color: ${props => props.color || "#228B22"};
  margin-bottom: 20px;
`;

const ServiceTitle = styled.h4`
  font-weight: 700;
  margin-bottom: 15px;
  color: #333;
`;

const ServiceDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
`;

const AdvantageCard = styled.div`
  padding: 25px 15px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const AdvantageIcon = styled.div`
  font-size: 48px;
  margin-bottom: 15px;
  color: ${props => props.color || "#0d6efd"};
`;

const Section = styled.section`
  padding: 80px 0;
  background: ${props => props.background || "white"};
`;

const FeatureList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  position: relative;
  padding-left: 28px;
  margin-bottom: 15px;
  color: #555;
  font-size: 16px;
  line-height: 1.6;
  
  &:before {
    content: "✓";
    position: absolute;
    left: 0;
    color: #008a44;
    font-weight: bold;
  }
`;

const AdministrationPublique = () => {
    const [activeIndex, setActiveIndex] = useState(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const faqSection = document.getElementById("faq-section");
            if (faqSection) {
                const rect = faqSection.getBoundingClientRect();
                if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) {
                    setIsVisible(true);
                } else {
                    setIsVisible(false);
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const toggleFAQ = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    const auditServices = [
        {
            icon: <FaFileAlt />,
            title: "Audit légal",
            description: "Certification des comptes des entités publiques soumises à cette obligation.",
            color: "#228B22"
        },
        {
            icon: <FaHandshake />,
            title: "Audit contractuel",
            description: "Missions d'audit spécifiques sur demande (audit de la performance, audit de la gestion, audit des systèmes d'information, etc.).",
            color: "#dc3545"
        },
        {
            icon: <FaChartLine />,
            title: "Évaluation des politiques publiques",
            description: "Analyse de l'efficacité, de l'efficience et de l'impact des politiques publiques.",
            color: "#0d6efd"
        },
        {
            icon: <FaShieldAlt />,
            title: "Contrôle interne",
            description: "Cartographie des risques, définition et mise en œuvre des procédures de contrôle, évaluation de l'efficacité du dispositif.",
            color: "#fd7e14"
        },
        {
            icon: <FaSearchDollar />,
            title: "Audit de la fraude",
            description: "Investigation en cas de suspicion de fraude ou de malversations.",
            color: "#6f42c1"
        }
    ];

    const consultingServices = [
        {
            icon: <FaLaptopCode />,
            title: "Assistance à maîtrise d'ouvrage",
            description: "Pour les projets de transformation numérique, de modernisation des services publics, de mise en place de nouvelles réglementations.",
            color: "#20c997"
        },
        {
            icon: <FaClipboardList />,
            title: "Conseil en organisation",
            description: "Optimisation des structures, des processus et des méthodes de travail.",
            color: "#fd7e14"
        },
        {
            icon: <FaUserShield />,
            title: "Gestion des ressources humaines",
            description: "Optimisation de la masse salariale, gestion prévisionnelle des emplois et des compétences (GPEC).",
            color: "#0dcaf0"
        },
        {
            icon: <FaEdit />,
            title: "Conseil en commande publique",
            description: "Assistance à la rédaction des cahiers des charges, analyse des offres, suivi de l'exécution des marchés publics.",
            color: "#6f42c1"
        },
        {
            icon: <FaHandshake />,
            title: "Partenariat public-privé",
            description: "Accompagnement des projets de PPP : analyse financière, structuration du montage, suivi de l'exécution.",
            color: "#dc3545"
        }
    ];

    const formationServices = [
        {
            icon: <FaGraduationCap />,
            title: "Formations sur-mesure",
            description: "Adaptées à vos besoins et à votre niveau de connaissance (comptabilité publique, finances locales, gestion budgétaire, commande publique, etc.).",
            color: "#0d6efd"
        },
        {
            icon: <FaUniversity />,
            title: "Formations inter- ou intra-établissements",
            description: "Pour vos agents et vos élus.",
            color: "#198754"
        }
    ];

    const advantagesData = [
        {
            icon: <FaUserShield />,
            title: "Expertise Sectorielle",
            color: "#0d6efd"
        },
        {
            icon: <FaShieldAlt />,
            title: "Conformité Réglementaire",
            color: "#198754"
        },
        {
            icon: <FaHandshake />,
            title: "Partenariat Durable",
            color: "#dc3545"
        },
        {
            icon: <FaLightbulb />,
            title: "Solutions Innovantes",
            color: "#ffc107"
        }
    ];

    const faqData = [
        {
            question: "Quels types d'organismes publics accompagnez-vous ?",
            answer: "Nous accompagnons tous les types d'organismes publics : ministères, collectivités territoriales, établissements publics, agences gouvernementales, entreprises publiques et parapubliques."
        },
        {
            question: "Quelle est la différence entre l'audit légal et l'audit contractuel pour le secteur public ?",
            answer: "L'audit légal est une mission obligatoire pour certaines entités publiques, visant à certifier la régularité, la sincérité et la fidélité des comptes. L'audit contractuel est une mission facultative, demandée par l'entité elle-même, pouvant porter sur un aspect spécifique de la gestion (performance, conformité, etc.)."
        },
        {
            question: "Comment nous adaptons-nous aux spécificités du secteur public ?",
            answer: "Notre équipe comprend des experts spécialisés dans le secteur public, maîtrisant les règles de la comptabilité publique, de la gestion budgétaire et de la commande publique. Nous nous tenons constamment informés des évolutions réglementaires et des bonnes pratiques du secteur."
        },
        {
            question: "Quels sont les avantages d'un audit pour une entité publique ?",
            answer: "L'audit permet de renforcer la transparence, d'identifier les risques et les axes d'amélioration, de garantir la fiabilité des comptes, d'optimiser les processus et de rassurer les parties prenantes (tutelles, citoyens, partenaires)."
        },
        {
            question: "Comment se déroule une mission d'audit dans le secteur public ?",
            answer: "La mission commence par une phase de planification et d'évaluation des risques, suivie d'une phase de collecte et d'analyse des preuves. Elle se conclut par la formulation de conclusions et de recommandations, présentées dans un rapport détaillé."
        },
        {
            question: "Les formations que vous proposez sont-elles certifiantes ?",
            answer: "Nos formations peuvent être certifiantes, selon vos besoins. Nous sommes en mesure de délivrer des attestations de formation et de vous accompagner dans l'obtention de certifications professionnelles reconnues dans le secteur public."
        }
    ];

    return (
        <>
            <Header />

            <section className="service-banner d-flex align-items-center">
                <div className="container-fluid px-4 px-md-5 text-white">
                    <div className="row">
                        <div className="col-lg-8">
                            <h1 className="fw-bold display-4 mb-3">Administration Publique</h1>
                            <p className="fs-5 mb-4">Un partenaire de confiance pour l'État, les collectivités territoriales et les établissements publics</p>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/" className="text-white text-decoration-none">
                                            Accueil
                                        </Link>{" "}
                                        /{" "}
                                        <Link to="/services" className="text-white text-decoration-none">
                                            Services
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item active text-white" aria-current="page">
                                        Administration Publique
                                    </li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row">
                        <div className="col-12">
                            <h2 className="fw-bold custom-title text-center mb-5">
                                La gestion des finances publiques exige rigueur, transparence et conformité aux normes spécifiques
                            </h2>
                            <p className="text-muted text-center fs-5 mb-5">
                                CINEF-EC dispose d'une expertise reconnue dans l'accompagnement des acteurs publics (ministères, collectivités, établissements publics, opérateurs de l'État, etc.). 
                                Nous mettons nos compétences au service de la performance et de la bonne gouvernance du secteur public.
                            </p>
                        </div>
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <div className="bg-light p-5 rounded-3">
                                <h3 className="fw-bold custom-title mb-4">
                                    Pourquoi faire appel à notre cabinet pour l'administration publique ?
                                </h3>
                                <div className="row">
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaUniversity className="fs-1 text-success" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Expertise du secteur public</h5>
                                                <p className="text-muted">
                                                    Notre équipe comprend des spécialistes du secteur public, 
                                                    maîtrisant les spécificités de la comptabilité publique et les normes en vigueur.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaUserShield className="fs-1 text-primary" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Connaissance des enjeux</h5>
                                                <p className="text-muted">
                                                    Nous comprenons les défis spécifiques du secteur public
                                                    et proposons des solutions adaptées aux contraintes budgétaires et réglementaires.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaChartLine className="fs-1 text-danger" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Approche sur-mesure</h5>
                                                <p className="text-muted">
                                                    Nous concevons nos missions en fonction des besoins spécifiques
                                                    de chaque entité publique, qu'il s'agisse d'un ministère, d'une collectivité ou d'un établissement public.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaHandshake className="fs-1 text-warning" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Accompagnement global</h5>
                                                <p className="text-muted">
                                                    Nous vous accompagnons dans toutes les dimensions de la gestion publique,
                                                    de l'audit à la formation, en passant par le conseil stratégique.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Section background="#f8f9fa">
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12 text-center">
                            <h2 className="fw-bold mb-1">Audit et Contrôle Interne</h2>
                            <div className="d-inline-block mb-4">
                                <div className="custom-underline mx-auto"></div>
                            </div>
                            <p className="text-muted fs-5 mb-5">Des solutions d'audit adaptées aux spécificités du secteur public</p>
                        </div>
                    </div>

                    <div className="row">
                        {auditServices.map((service, index) => (
                            <div className="col-md-6 col-lg-4 mb-4" key={index}>
                                <ServiceCard delay={`${index * 0.1}s`}>
                                    <ServiceIcon color={service.color}>{service.icon}</ServiceIcon>
                                    <ServiceTitle>{service.title}</ServiceTitle>
                                    <ServiceDescription>{service.description}</ServiceDescription>
                                </ServiceCard>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12 text-center">
                            <h2 className="fw-bold mb-1">Conseil et Accompagnement Stratégique</h2>
                            <div className="d-inline-block mb-4">
                                <div className="custom-underline mx-auto"></div>
                            </div>
                            <p className="text-muted fs-5 mb-5">Des solutions adaptées aux besoins spécifiques du secteur public</p>
                        </div>
                    </div>

                    <div className="row">
                        {consultingServices.map((service, index) => (
                            <div className="col-md-6 col-lg-4 mb-4" key={index}>
                                <ServiceCard delay={`${index * 0.1}s`}>
                                    <ServiceIcon color={service.color}>{service.icon}</ServiceIcon>
                                    <ServiceTitle>{service.title}</ServiceTitle>
                                    <ServiceDescription>{service.description}</ServiceDescription>
                                </ServiceCard>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section background="#f8f9fa">
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12 text-center">
                            <h2 className="fw-bold mb-1">Formations</h2>
                            <div className="d-inline-block mb-4">
                                <div className="custom-underline mx-auto"></div>
                            </div>
                            <p className="text-muted fs-5 mb-5">Des formations adaptées aux besoins des acteurs publics</p>
                        </div>
                    </div>

                    <div className="row justify-content-center">
                        {formationServices.map((service, index) => (
                            <div className="col-md-6 mb-4" key={index}>
                                <ServiceCard delay={`${index * 0.1}s`}>
                                    <ServiceIcon color={service.color}>{service.icon}</ServiceIcon>
                                    <ServiceTitle>{service.title}</ServiceTitle>
                                    <ServiceDescription>{service.description}</ServiceDescription>
                                </ServiceCard>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Footer />

            <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.85), rgba(220, 53, 69, 0.85)), 
                      url("/assets/images/image-administration-publique.png") no-repeat center center;
          background-size: cover;
          background-position: center;
          height: 350px;
          display: flex;
          align-items: center;
        }

        .breadcrumb {
          background: transparent;
          font-size: 18px;
        }

        .breadcrumb-item a {
          color: white;
          font-weight: 500;
        }

        .custom-title {
          position: relative;
          padding-bottom: 15px;
        }

        .custom-title:after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 60px;
          height: 3px;
          background: linear-gradient(to right, #008a44, #dc3545);
        }

        .text-center .custom-title:after {
          left: 50%;
          transform: translateX(-50%);
        }

        .custom-underline {
          height: 4px;
          width: 70px;
          background: linear-gradient(to right, #008a44, #dc3545);
          margin-top: 10px;
        }

        .call-to-action {
          background: linear-gradient(to right, #008a44, #dc3545);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        #faq-section {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        #faq-section.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        @media (max-width: 768px) {
          .service-banner {
            height: 350px;
          }
          
          .custom-title {
            font-size: 1.75rem;
          }
        }
      `}</style>
        </>
    );
};

export default AdministrationPublique;